import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { SiteConfig, ApiResponse } from '@crypto-factory/shared-types';

const PROD_API = 'https://crypto-factory-api.cryptoramosv2.workers.dev';
const API_BASE = import.meta.env.VITE_API_URL || PROD_API;

console.log('Using API Base:', API_BASE); // Debug log

/**
 * Fetches site configuration from the API with timeout
 */
async function fetchConfig(): Promise<Partial<SiteConfig>> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000); // 10s timeout

    try {
        const res = await fetch(`${API_BASE}/api/config`, {
            signal: controller.signal
        });
        clearTimeout(timeoutId);

        if (!res.ok) throw new Error('Failed to fetch config');
        const data: ApiResponse<Partial<SiteConfig>> = await res.json();
        if (!data.success) throw new Error(data.error || 'Unknown error');
        return data.data!;
    } catch (error) {
        clearTimeout(timeoutId);
        throw error;
    }
}

/**
 * Hook to access site configuration
 */
export function useConfig() {
    // Skip fetching for demo routes - they don't need config
    const isDemoRoute = typeof window !== 'undefined' && window.location.pathname.startsWith('/demo');

    const query = useQuery({
        queryKey: ['config'],
        queryFn: fetchConfig,
        staleTime: 1000 * 60 * 5, // 5 minutes
        retry: 1,
        enabled: !isDemoRoute, // Don't fetch for demo routes
    });

    // Parse config into typed structure
    const config: Partial<SiteConfig> = query.data
        ? {
            archetype: query.data.archetype as SiteConfig['archetype'],
            theme: query.data.theme as SiteConfig['theme'],
            features: query.data.features as SiteConfig['features'],
            content: query.data.content as SiteConfig['content'],
            socials: query.data.socials as SiteConfig['socials'],
            tickerSymbols: query.data.tickerSymbols as string[],
            isMaintenanceMode: query.data.isMaintenanceMode as boolean,
        }
        : {};

    return {
        config,
        rawConfig: query.data,
        isLoading: query.isLoading,
        isError: query.isError,
        error: query.error,
        refetch: query.refetch,
    };
}

/**
 * Hook to update site configuration (admin only)
 */
export function useUpdateConfig() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (updates: { key: string; value: unknown }[]) => {
            const token = await getAuthToken();
            const res = await fetch(`${API_BASE}/api/admin/config`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({ updates }),
            });
            if (!res.ok) throw new Error('Failed to update config');
            return res.json();
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['config'] });
        },
    });
}

// Helper to get auth token from Clerk
async function getAuthToken(): Promise<string> {
    // @ts-expect-error Clerk global instance
    const clerk = window.Clerk;
    if (!clerk?.session) {
        throw new Error('Not authenticated');
    }
    return clerk.session.getToken() || '';
}
